import React, { useState, useEffect, useCallback } from "react";
import Editor from "@monaco-editor/react";
import axios from "axios";
import TabbedIO from "./TabbedIO";
import Gemini from "./Gemini";
import { theme } from "../theme";
import { API_CONFIG, LANGUAGE_CONFIG, CODE_TEMPLATES } from "../config/api";

const CodeEditorWithJudge0 = () => {
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("java");
  const [languageId, setLanguageId] = useState(62);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("");
  
  // Store code for each language
  const [languageCode, setLanguageCode] = useState({
    cpp: CODE_TEMPLATES.cpp,
    java: CODE_TEMPLATES.java,
    javascript: CODE_TEMPLATES.javascript,
    c: CODE_TEMPLATES.c,
    python: CODE_TEMPLATES.python
  });

  const [code, setCode] = useState(CODE_TEMPLATES.java);
  
  // Update code in state and languageCode when user types
  const handleCodeChange = (newValue) => {
    setCode(newValue);
    setLanguageCode(prev => ({
      ...prev,
      [language]: newValue
    }));
  };
  

const handleLanguageChange = (event) => {
    const selectedLanguage = event.target.value;
    
    // Save current code before switching
    setLanguageCode(prev => ({
      ...prev,
      [language]: code
    }));
    
    // Switch to new language
    setLanguage(selectedLanguage);
    setCode(languageCode[selectedLanguage] || CODE_TEMPLATES[selectedLanguage]);
    setLanguageId(LANGUAGE_CONFIG[selectedLanguage].id);
  };


  const handleRunCode = useCallback(async () => {
    setLoading(true);
    setOutput(""); 

    try {
      console.log(code);
  
      const { data } = await axios.post(
        API_CONFIG.JUDGE0.BASE_URL,
        {
          source_code: code,
          language_id: languageId, 
          stdin: input,
        },
        {
          headers: {
            "X-RapidAPI-Host": API_CONFIG.JUDGE0.HOST,
            "X-RapidAPI-Key": API_CONFIG.JUDGE0.KEY,
            "Content-Type": "application/json",
          },
        }
      );

      const { token } = data;
      let result;
      do {
        result = await axios.get(
          API_CONFIG.JUDGE0.RESULT_URL(token),
          {
            headers: {
              "X-RapidAPI-Host": API_CONFIG.JUDGE0.HOST,
              "X-RapidAPI-Key": API_CONFIG.JUDGE0.KEY,
            },
          }
        );
        console.log(result.data);
        await new Promise((resolve) => setTimeout(resolve, 1000)); 
      } while (result.data.status.id <= 2); 

     
      // Handle different types of output and errors
      let finalOutput = "";
      let finalError = "";

      if (result.data.stdout) {
        finalOutput = atob(result.data.stdout);
      }
      
      if (result.data.stderr) {
        finalError = atob(result.data.stderr);
      }
      
      if (result.data.compile_output) {
        finalError = atob(result.data.compile_output);
      }

      // Set output and error based on status
      if (result.data.status.id === 6) { // Compile error
        finalError = finalError || "Compilation error occurred";
        setError(finalError);
        setErrorType("compile");
        setOutput("");
      } else if (result.data.status.id === 11) { // Runtime error
        finalError = finalError || "Runtime error occurred";
        setError(finalError);
        setErrorType("runtime");
        setOutput("");  // Don't show output for runtime errors, only the error
      } else if (result.data.status.id === 3) { // Success
        setOutput(finalOutput);
        setError("");
        setErrorType("");
      } else {
        // Other statuses
        if (finalError) {
          setError(finalError);
          setErrorType("error");
          setOutput("");
        } else {
          setOutput(finalOutput);
          setError("");
          setErrorType("");
        }
      }
      
    } catch (error) {
      setOutput("");
      setError("Error executing code: " + (error.response?.data?.message || error.message));
      setErrorType("error");
    } finally {
      setLoading(false);
      console.log("log output : " + output);
    }
  }, [code, input, languageId, output]);
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleRunCode();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [code, input, languageId, handleRunCode]);

  return (
    <div 
      className="flex flex-col h-screen"
      style={{ backgroundColor: theme.colors.bg.primary }}
    >
      {/* Toolbar */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b gap-3"
        style={{ 
          backgroundColor: theme.colors.bg.secondary,
          borderColor: theme.colors.border.primary 
        }}
      >
        <div className="flex items-center space-x-4">
          {/* Language Selector */}
          <div className="flex items-center space-x-2">
            <label 
              className="text-sm font-medium"
              style={{ color: theme.colors.text.secondary }}
            >
              Language:
            </label>
            <select
              onChange={handleLanguageChange}
              value={language}
              className="px-3 py-1.5 text-sm rounded-md border outline-none transition-colors"
              style={{
                color: theme.colors.text.primary,
                backgroundColor: theme.colors.bg.tertiary,
                borderColor: theme.colors.border.primary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              <option value="cpp">C++</option>
              <option value="java">Java</option>
              <option value="javascript">JavaScript</option>
              <option value="c">C</option>
              <option value="python">Python</option>
            </select>
          </div>
          
          <button
            onClick={handleRunCode}
            disabled={loading}
            className="px-4  py-2 text-sm font-medium rounded-md transition-all"
            style={{
              backgroundColor: loading ? theme.colors.border.primary : theme.colors.accent.green,
              color: theme.colors.text.inverse,
              fontFamily: theme.typography.fontFamily.sans.join(', '),
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Running...' : 'Run Code'}
          </button>
          
          {/* <span 
            className="text-xs"
            style={{ color: theme.colors.text.tertiary }}
          >
            Ctrl+Enter to run
          </span> */}
        </div>

        <div className="flex items-center space-x-3">
          <Gemini code={code} />
        </div>
      </div>

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Editor Panel */}
        <div className="flex-1 lg:flex-initial lg:w-3/5 border-r lg:border-r-0 lg:border-b border-b">
          <Editor
            height="100%"
            defaultLanguage="java"
            language={language}
            theme="vs-dark"
            value={code}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "on",
              automaticLayout: true,
              scrollBeyondLastLine: false,
              renderWhitespace: "selection",
              bracketPairColorization: { enabled: true },
              guides: {
                indentation: true,
                bracketPairs: false
              },
              inlineSuggest: true,
              formatOnType: true,
              autoClosingBrackets: true,
              suggest: {
                showKeywords: false,
                showSnippets: false
              },
              quickSuggestions: false, // Disable command palette
              parameterHints: { enabled: false } // Disable parameter hints
            }}
            onChange={handleCodeChange}
          />
        </div>
        
        {/* Input/Output Panel */}
        <div className="flex-1 lg:flex-initial lg:w-2/5 lg:min-w-[20%]">
          <TabbedIO
            input={input}
            output={output}
            onInputChange={setInput}
            loading={loading}
            error={error}
            errorType={errorType}
            autoSwitchToOutput={true}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeEditorWithJudge0;
